import { useServiceAccess } from '../access/useAccess.ts'
import { useServiceTypePlace } from './usePlansPage.ts'
import { useTeams, type Team } from './useServices.ts'

/**
 * A service type's settings page (docs/14, G3b).
 *
 * The settings page is its Administrators': General, Times, Teams,
 * Templates, Access. Its Teams are the teams serving it — each changed only
 * by an Administrator of **every** service type it serves (G1's rule), so
 * some are read-only here, with the reason. Every team serves at least one
 * service type (2026-10-21: no church-wide teams, as in Planning Center).
 */

/** A team as the settings pages list it: what it is, and what the viewer may do with it. */
export interface TeamRow {
  team: Team
  /** They may change it: Administrator on every service type it serves. */
  editable: boolean
  /** Why not, in one line; null when they may. */
  lockedBecause: string | null
  /**
   * Where it serves, as they can see it: "Sunday Morning · Youth Night"; from
   * a service type's settings, the others ("Also Youth Night"); empty for
   * one serving only here.
   */
  where: string
  /** "4 positions · Led by David Okafor". */
  summary: string
}

/** "Sunday Evening and Youth Night". */
function listOf(names: readonly string[]) {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''}`
}

function useTeamRows() {
  const access = useServiceAccess()
  const teams = useTeams()
  const typeName = (id: string) => access.types.find((type) => type.id === id)?.name
  const row = (team: Team, here: string | null): TeamRow => {
    const editable = access.canEditTeam(team)
    let lockedBecause: string | null = null
    if (!editable) {
      const missing = team.serviceTypeIds.filter((id) => access.planning(id) !== 'administrator')
      const named = missing.map(typeName).filter((name): name is string => Boolean(name))
      const unseen = missing.length - named.length
      const where = [
        ...named,
        ...(unseen > 0
          ? [unseen === 1 ? 'a service you can’t see' : 'services you can’t see']
          : []),
      ]
      lockedBecause = `Read-only: it also serves ${listOf(where)}, where you aren’t an Administrator.`
    }
    const positions = team.positions.length
    return {
      team,
      editable,
      lockedBecause,
      where: (() => {
        const names = team.serviceTypeIds
          .filter((id) => id !== here)
          .map(typeName)
          .filter((name): name is string => Boolean(name))
        if (names.length === 0) return ''
        return here === null ? names.join(' · ') : `Also ${names.join(' · ')}`
      })(),
      summary: [
        `${positions} ${positions === 1 ? 'position' : 'positions'}`,
        team.leaders.length > 0 && `Led by ${team.leaders.map((leader) => leader.name).join(', ')}`,
      ]
        .filter(Boolean)
        .join(' · '),
    }
  }
  return { access, teams, row }
}

/**
 * One service type's settings: whether it is there for them (a hidden one
 * is not), whether they may open its settings (its Administrators), and
 * where it sits, for the breadcrumbs.
 */
export function useServiceTypeSettings(typeId: string) {
  const access = useServiceAccess()
  const place = useServiceTypePlace(typeId)
  return {
    /** The folder tree has arrived: levels are final. */
    ready: access.ready,
    type: place.type,
    folders: place.folders,
    /** Its settings are its Administrators' (the server answers 403 to anyone else). */
    allowed: place.type !== null && access.can(typeId, 'administrator'),
    /** They may move it up and down the church's own order (church-wide Administrator). */
    canReorder: access.planningInFolder(null) === 'administrator',
  }
}

/**
 * A service type's Teams section: the teams serving it, and the teams they
 * could add to it ("Add existing team": teams in other service types that
 * they may change).
 */
export function useServiceTypeTeams(typeId: string) {
  const { access, teams, row } = useTeamRows()
  const all = teams.data ?? []
  return {
    isPending: teams.isPending,
    error: teams.error,
    serving: all.filter((team) => team.serviceTypeIds.includes(typeId)).map((t) => row(t, typeId)),
    /** Teams in other service types they may change: each can serve here too. */
    addable: all
      .filter((team) => !team.serviceTypeIds.includes(typeId) && access.canEditTeam(team))
      .map((t) => row(t, null)),
    /** "+ New team" here: its Administrators. */
    canAdd: access.can(typeId, 'administrator'),
    /**
     * Taking a team off this service type: changing it, and never its last
     * one (a team serves at least one; the server answers 422, so Delete
     * is what remains there).
     */
    canRemove: (team: Team) =>
      team.serviceTypeIds.includes(typeId) &&
      access.canEditTeam(team) &&
      team.serviceTypeIds.length > 1,
  }
}
